import type {
  FormFieldMessage,
  FormFieldMessageContextState,
  FormFieldControlMessage,
  FormFieldCustomMessage,
} from '../contexts/FormFieldMessageContext.js';

export const enum FormFieldMessagesAction {
  ADDMESSAGE = 'addmessage',
  REMOVEMESSAGE = 'removemessage',
  DISABLEMESSAGE = 'disablemessage',
  SETISTOUCHED = 'setistouched',
}

export type FormFieldMessagesReducerAction =
  | {
      type: FormFieldMessagesAction.ADDMESSAGE;
      payload: (
        | Omit<FormFieldControlMessage, 'id'>
        | FormFieldCustomMessage
      ) & { showError?: boolean };
    }
  | {
      type: FormFieldMessagesAction.REMOVEMESSAGE;
      payload: Pick<FormFieldMessage, 'id' | 'messageType' | 'controlId'>;
    }
  | {
      type: FormFieldMessagesAction.DISABLEMESSAGE;
      payload: {
        isDisabled: boolean | undefined;
      };
    }
  | {
      type: FormFieldMessagesAction.SETISTOUCHED;
      payload: { isTouched: boolean };
    };

type HTMLFormControlElement =
  | HTMLSelectElement
  | HTMLInputElement
  | HTMLTextAreaElement;

/**
 * Typeguard function that will check if message is a formcontrol (internal) message.
 * @internal
 */
function isFormControlPayload(
  payload:
    | Omit<FormFieldControlMessage, 'id'>
    | FormFieldCustomMessage
    | FormFieldControlMessage,
  messageType?: 'formcontrol' | 'custom',
): payload is FormFieldControlMessage | Omit<FormFieldControlMessage, 'id'> {
  return messageType === 'formcontrol';
}

/**
 * Typeguard function that will check if the element is a form control.
 * @internal
 */
function isHTMLFormControlElement(
  element: null | HTMLElement,
): element is HTMLFormControlElement {
  return !!(element && 'setCustomValidity' in element);
}

/**
 * Function that will determine the validity of a form control.
 * @internal
 */
function updateValidity(
  state: FormFieldMessageContextState,
  explicitControlId?: string,
) {
  const { customMessages, formControlMessages, isTouched, isDisabled } = state;
  const controlId = explicitControlId ?? state.controlId ?? '';

  const hasCustomError = customMessages.some(
    (message) => message.variant === 'error',
  );

  const hasFormControlError = formControlMessages.some(
    (message) => message.variant === 'error',
  );

  const newIsValid = !hasCustomError && !hasFormControlError;
  const newShowError =
    !isDisabled && (state.showError || (isTouched ?? false)) && !newIsValid;

  const formControlElement = document.getElementById(controlId);
  if (isHTMLFormControlElement(formControlElement)) {
    formControlElement.setCustomValidity(newIsValid ? '' : ' ');
  }

  return {
    ...state,
    isValid: newIsValid,
    showError: newShowError,
  };
}

/** FormFieldMessage reducer function used for managing FormFieldMessages and FormFieldCustomMessages. */
export function formFieldMessagesReducer(
  state: FormFieldMessageContextState,
  action: FormFieldMessagesReducerAction,
  controlId: string,
): FormFieldMessageContextState {
  const newState = { ...state, controlId };

  // handle adding of form messages
  if (action.type === FormFieldMessagesAction.ADDMESSAGE) {
    const { payload } = action;
    const { messageType, ...remainingPayload } = payload;

    // Is a browser/strato message
    if (isFormControlPayload(remainingPayload, messageType)) {
      // browser errors come with a unique validityStateKey
      const isNewBrowserError =
        remainingPayload.validityStateKey !== undefined &&
        remainingPayload.validityStateKey !== 'customError' &&
        !state.formControlMessages.some(
          (message) =>
            message.validityStateKey === remainingPayload.validityStateKey,
        );

      const isNewInternalMessage =
        remainingPayload.validityStateKey === 'customError' &&
        'id' in remainingPayload &&
        !state.formControlMessages.some(
          (message) => message.id === remainingPayload.id,
        );

      if (isNewBrowserError || isNewInternalMessage) {
        newState.formControlMessages.push({
          id: `strato-${crypto.randomUUID()}`,
          ...remainingPayload,
        });
      }
      // Is a custom error
    } else {
      if (
        'id' in remainingPayload &&
        !state.formControlMessages.some(
          (message) => message.id === remainingPayload.id,
        ) &&
        !state.customMessages.some(
          (message) => message.id === remainingPayload.id,
        )
      ) {
        newState.customMessages.push(remainingPayload);
      }
    }

    if (remainingPayload.variant === 'error') {
      newState.showError = payload.showError ?? state.showError;
    }

    return updateValidity(newState, payload.controlId);
  }

  if (action.type === FormFieldMessagesAction.REMOVEMESSAGE) {
    const { payload } = action;

    // remove the message from formcontrol or custom messages
    if (payload.messageType === 'formcontrol') {
      newState.formControlMessages = state.formControlMessages.filter(
        (message) => message.id !== payload.id,
      );
    } else {
      newState.customMessages = state.customMessages.filter(
        (message) => message.id !== payload.id,
      );
    }

    return updateValidity(newState, payload.controlId);
  }

  if (action.type === FormFieldMessagesAction.DISABLEMESSAGE) {
    const { payload } = action;
    newState.isDisabled = payload.isDisabled;
  }

  if (action.type === FormFieldMessagesAction.SETISTOUCHED) {
    const { payload } = action;
    newState.isTouched = payload.isTouched;
  }

  return updateValidity(newState);
}
